import React, { forwardRef } from "react";
import type { FC, JSX, ComponentProps } from "react";

import type { PropsWithTsId } from "../types/react";

function createComponentPreset<T extends FC<any>>(
    displayName: string,
    Component: FC<any>,
    preparedProps?: PropsWithTsId<ComponentProps<T>>,
): T;

// Для композитных компонентов, помимо самого компонента, сохраняем ещё и всё его содержимое
function createComponentPreset<
    T extends FC<any>,
    C extends FC<any> & {
        [ComponentName in keyof C]: C[ComponentName];
    },
>(
    displayName: string,
    Component: C,
    preparedProps?: PropsWithTsId<ComponentProps<T>>,
): C & T;

/**
 * Фабрика по созданию пресетов из готовых компонентов. Поддерживаются как обычные, так и композитные компоненты.
 */
function createComponentPreset<
    T extends FC<any>,
    C extends
        | FC<any>
        | (FC<any> & {
              [ComponentName in keyof C]: C[ComponentName];
          }),
>(
    displayName: string,
    Component: C,
    preparedProps?: PropsWithTsId<ComponentProps<T>>,
): T | (C & T) {
    const EnrichedComponent = forwardRef<HTMLElement, ComponentProps<T>>(
        ({ ...props }, ref): JSX.Element => {
            // @ts-expect-error - наверняка не знаем: будет ref или нет, но на всякий случай передаём его
            // eslint-disable-next-line ok-custom-rules/check-attribute
            return <Component {...preparedProps} {...props} ref={ref} />;
        },
    );
    EnrichedComponent.displayName = displayName;

    Object.keys(Component).forEach((key) => {
        if (key in EnrichedComponent) {
            return;
        }
        // @ts-expect-error - наверняка не можем настроить, какие ключи и значения нам придут из Component
        EnrichedComponent[key] = Component[key];
    });

    return EnrichedComponent as unknown as T;
}

export default createComponentPreset;
